import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, KeyRound, CheckCircle2 } from "lucide-react";

/**
 * ResetPassword
 * -------------
 * Landed-on from the email link. Reads `token` from the query string,
 * collects + confirms a new password, and posts to /auth/reset-password.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Missing reset token in link.");
      return;
    }
    if (pw.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (pw !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-slate-900 p-6" data-testid="reset-password-page">
      <Card className="w-full max-w-md p-8 sm:p-10 border-slate-200">
        {done ? (
          <div className="text-center" data-testid="reset-success">
            <div className="mx-auto h-16 w-16 rounded-2xl bg-emerald-100 grid place-items-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-700" />
            </div>
            <h1 className="font-display text-2xl font-bold tracking-tight">Password updated</h1>
            <p className="text-slate-600 mt-2">
              You can now log in with your new password.
            </p>
            <Button
              onClick={() => nav("/login")}
              className="w-full mt-6 h-11 bg-[#1E3A8A] hover:bg-[#1E40AF]"
              data-testid="reset-go-login-btn"
            >
              Go to login
            </Button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-1">
              <KeyRound className="w-5 h-5 text-indigo-600" />
              <h1 className="font-display text-2xl font-bold tracking-tight">Set a new password</h1>
            </div>
            <p className="text-slate-600 mt-1 text-sm">
              Choose a strong password — at least 8 characters.
            </p>
            <form onSubmit={submit} className="space-y-4 mt-6">
              <div>
                <Label htmlFor="new-pw">New password</Label>
                <Input
                  id="new-pw"
                  type="password"
                  required
                  minLength={8}
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  autoComplete="new-password"
                  data-testid="reset-new-password-input"
                />
              </div>
              <div>
                <Label htmlFor="confirm-pw">Confirm new password</Label>
                <Input
                  id="confirm-pw"
                  type="password"
                  required
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  data-testid="reset-confirm-password-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#1E3A8A] hover:bg-[#1E40AF] h-11"
                disabled={loading}
                data-testid="reset-submit-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
              </Button>
            </form>
            <p className="mt-6 text-sm text-slate-600 text-center">
              <Link to="/login" className="hover:underline">← Back to login</Link>
            </p>
          </>
        )}
      </Card>
    </div>
  );
}
